import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Spin, message } from 'antd';
import { authAPI } from '../services/api';
import NotFound from '../pages/NotFound';

interface AdminRouteProps {
  children: React.ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading, logout, allowedPaths } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  useEffect(() => {
    const verifyAdminPermission = async () => {
      if (loading) {
        return;
      }

      if (!user) {
        setVerifying(false);
        return;
      }

      if (!allowedPaths.has('/admin')) {
        setIsAdmin(false);
        setVerifyError('您没有管理员权限');
        setVerifying(false);
        return;
      }

      try {
        const response = await authAPI.verifyAdmin();
        
        if (response.data.status === 200) {
          const { isAdmin: adminStatus, user: verifiedUser } = response.data;
          
          if (adminStatus) {
            setIsAdmin(true);
            // 更新用户信息，确保前端状态与后端一致
            if (verifiedUser) {
              const storedUser = localStorage.getItem('user');
              if (storedUser) {
                try {
                  const userObj = JSON.parse(storedUser);
                  userObj.is_admin = verifiedUser.is_admin;
                  localStorage.setItem('user', JSON.stringify(userObj));
                } catch (e) {
                  console.error('更新用户信息失败:', e);
                }
              }
            }
          } else {
            setIsAdmin(false);
            setVerifyError('您没有管理员权限');
            // 清除用户信息并退出登录
            logout();
            message.error('您没有管理员权限，已自动退出登录');
          }
        } else {
          setIsAdmin(false);
          setVerifyError(response.data.error || '权限验证失败');
          // 如果验证失败，清除用户信息并退出登录
          logout();
          message.error(response.data.error || '权限验证失败，已自动退出登录');
        }
      } catch (error: any) {
        console.error('验证管理员权限失败:', error);
        setIsAdmin(false);
        setVerifyError(error.response?.data?.error || '权限验证失败');
        // 验证失败时，清除用户信息并退出登录
        logout();
        message.error('权限验证失败，已自动退出登录');
      } finally {
        setVerifying(false);
      }
    };

    verifyAdminPermission();
  }, [user, loading, logout]);

  if (loading || verifying) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" tip="正在验证管理员权限..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin || verifyError) {
    return <NotFound />;
  }

  return <>{children}</>;
};

export default AdminRoute;














